import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Calendar, User as UserIcon, Sparkles, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('session_token');
        const headers = {
          'Authorization': `Bearer ${token}`
        };

        const [ordersRes, bookingsRes, subscriptionsRes] = await Promise.all([
          fetch(`${API}/orders`, { headers, credentials: 'include' }),
          fetch(`${API}/bookings`, { headers, credentials: 'include' }),
          fetch(`${API}/subscriptions/my`, { headers, credentials: 'include' })
        ]);

        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          setOrders(ordersData);
        }

        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          setBookings(bookingsData);
        }

        if (subscriptionsRes.ok) {
          const subscriptionsData = await subscriptionsRes.json();
          setSubscriptions(subscriptionsData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load your data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCancelSubscription = async (subscriptionId) => {
    setCancellingId(subscriptionId);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Subscription cancelled successfully');
        // Update local state
        setSubscriptions(prev => prev.map(sub => 
          sub.subscription_id === subscriptionId 
            ? { ...sub, status: 'cancelled' } 
            : sub
        ));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-status-success/20 text-status-success">
            <CheckCircle className="w-3.5 h-3.5" />
            Active
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-text-muted">
            <XCircle className="w-3.5 h-3.5" />
            Cancelled
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-status-warning/20 text-status-warning">
            <AlertCircle className="w-3.5 h-3.5" />
            Pending
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-text-muted">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center mb-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full mr-4 border-2 border-primary" data-testid="user-avatar" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mr-4 border-2 border-primary">
                <UserIcon className="w-8 h-8 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-4xl font-heading font-bold text-white" data-testid="dashboard-welcome">Welcome, {user?.name}</h1>
              <p className="text-text-secondary font-body" data-testid="user-email">{user?.email}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid="orders-stat">
              <div className="flex items-center">
                <div className="p-3 bg-primary/10 rounded-xl mr-4">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-sm">Total Orders</p>
                  <p className="text-3xl font-heading font-bold text-white" data-testid="total-orders-count">{orders.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid="bookings-stat">
              <div className="flex items-center">
                <div className="p-3 bg-primary/10 rounded-xl mr-4">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-sm">Total Bookings</p>
                  <p className="text-3xl font-heading font-bold text-white" data-testid="total-bookings-count">{bookings.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-background-paper rounded-2xl p-6 border border-primary/20" data-testid="subscriptions-stat">
              <div className="flex items-center">
                <div className="p-3 bg-primary/10 rounded-xl mr-4">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted font-body text-sm">Active Subscriptions</p>
                  <p className="text-3xl font-heading font-bold text-white" data-testid="total-subscriptions-count">
                    {subscriptions.filter(s => s.status === 'active').length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="mb-6" data-testid="dashboard-tabs">
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 font-heading font-semibold transition-colors ${activeTab === 'orders' ? 'text-primary border-b-2 border-primary' : 'text-text-muted hover:text-white'}`}
              data-testid="orders-tab"
            >
              Orders
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`px-6 py-3 font-heading font-semibold transition-colors ${activeTab === 'bookings' ? 'text-primary border-b-2 border-primary' : 'text-text-muted hover:text-white'}`}
              data-testid="bookings-tab"
            >
              Bookings
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-6 py-3 font-heading font-semibold transition-colors ${activeTab === 'subscriptions' ? 'text-primary border-b-2 border-primary' : 'text-text-muted hover:text-white'}`}
              data-testid="subscriptions-tab"
            >
              Subscriptions
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'orders' && (
          <div className="space-y-4" data-testid="orders-section">
            {orders.length === 0 ? (
              <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20" data-testid="no-orders-message">
                <Package className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary font-body">No orders yet</p>
              </div>
            ) : (
              orders.map(order => (
                <motion.div
                  key={order.order_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-background-paper rounded-2xl p-6 border border-primary/20"
                  data-testid={`order-${order.order_id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-white" data-testid={`order-id-${order.order_id}`}>Order #{order.order_id}</h3>
                      <p className="text-sm text-text-muted font-body" data-testid={`order-date-${order.order_id}`}>{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-body font-medium ${order.status === 'paid' ? 'bg-status-success/20 text-status-success' : order.status === 'pending' ? 'bg-status-warning/20 text-status-warning' : 'bg-white/10 text-text-muted'}`} data-testid={`order-status-${order.order_id}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="space-y-2 mb-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm font-body" data-testid={`order-item-${order.order_id}-${idx}`}>
                        <span className="text-text-secondary">{item.product_name} x{item.quantity}</span>
                        <span className="text-white font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <div className="flex justify-between text-lg font-heading font-bold">
                      <span className="text-white">Total</span>
                      <span className="text-primary" data-testid={`order-total-${order.order_id}`}>£{order.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="space-y-4" data-testid="bookings-section">
            {bookings.length === 0 ? (
              <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20" data-testid="no-bookings-message">
                <Calendar className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary font-body">No bookings yet</p>
              </div>
            ) : (
              bookings.map(booking => (
                <motion.div
                  key={booking.booking_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-background-paper rounded-2xl p-6 border border-primary/20"
                  data-testid={`booking-${booking.booking_id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-white" data-testid={`booking-name-${booking.booking_id}`}>{booking.experience_name}</h3>
                      <p className="text-sm text-text-muted font-body mt-1" data-testid={`booking-id-${booking.booking_id}`}>Booking #{booking.booking_id}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-body font-medium ${booking.status === 'confirmed' ? 'bg-status-success/20 text-status-success' : booking.status === 'pending' ? 'bg-status-warning/20 text-status-warning' : 'bg-white/10 text-text-muted'}`} data-testid={`booking-status-${booking.booking_id}`}>
                      {booking.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm font-body">
                      <Calendar className="w-4 h-4 mr-2 text-text-muted" />
                      <span className="text-text-secondary" data-testid={`booking-date-${booking.booking_id}`}>{booking.booking_date} at {booking.time_slot}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/10">
                      <span className="text-text-secondary font-body">Price</span>
                      <span className="text-xl font-accent text-primary" data-testid={`booking-price-${booking.booking_id}`}>£{booking.price.toFixed(2)}</span>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="space-y-4" data-testid="subscriptions-section">
            {subscriptions.length === 0 ? (
              <div className="text-center py-12 bg-background-paper rounded-2xl border border-primary/20" data-testid="no-subscriptions-message">
                <Sparkles className="w-16 h-16 text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary font-body mb-4">No subscriptions yet</p>
                <Link to="/subscriptions">
                  <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-6" data-testid="browse-subscriptions-button">
                    Browse Subscription Plans
                  </Button>
                </Link>
              </div>
            ) : (
              subscriptions.map(subscription => (
                <motion.div
                  key={subscription.subscription_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-background-paper rounded-2xl p-6 border border-primary/20"
                  data-testid={`subscription-${subscription.subscription_id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-white capitalize" data-testid={`subscription-plan-${subscription.subscription_id}`}>
                        {subscription.plan} Plan
                      </h3>
                      <p className="text-sm text-text-muted font-body mt-1" data-testid={`subscription-id-${subscription.subscription_id}`}>
                        ID: {subscription.subscription_id}
                      </p>
                    </div>
                    {getStatusBadge(subscription.status)}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-background-elevated rounded-xl p-4">
                      <p className="text-text-muted text-sm font-body mb-1">Monthly Price</p>
                      <p className="text-2xl font-accent text-primary" data-testid={`subscription-price-${subscription.subscription_id}`}>
                        £{subscription.price?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="bg-background-elevated rounded-xl p-4">
                      <p className="text-text-muted text-sm font-body mb-1">Started</p>
                      <p className="text-white font-medium" data-testid={`subscription-start-${subscription.subscription_id}`}>
                        {new Date(subscription.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-background-elevated rounded-xl p-4">
                      <p className="text-text-muted text-sm font-body mb-1">Next Billing</p>
                      <p className="text-white font-medium" data-testid={`subscription-next-billing-${subscription.subscription_id}`}>
                        {subscription.next_billing_date 
                          ? new Date(subscription.next_billing_date).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {subscription.status === 'active' && (
                    <div className="flex justify-end pt-4 border-t border-white/10">
                      <Button
                        variant="outline"
                        onClick={() => handleCancelSubscription(subscription.subscription_id)}
                        disabled={cancellingId === subscription.subscription_id}
                        className="border-status-error/50 text-status-error hover:bg-status-error/10 hover:border-status-error rounded-full"
                        data-testid={`cancel-subscription-${subscription.subscription_id}`}
                      >
                        {cancellingId === subscription.subscription_id ? 'Cancelling...' : 'Cancel Subscription'}
                      </Button>
                    </div>
                  )}

                  {subscription.status === 'cancelled' && (
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-text-muted text-sm font-body">
                        This subscription has been cancelled. 
                        <Link to="/subscriptions" className="text-primary hover:text-primary-hover ml-1">
                          Browse new plans →
                        </Link>
                      </p>
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
